/**
 * Zero-Knowledge Vault Encryption Service
 *
 * Implements client-side encryption so NOT EVEN THE ADMIN can see vault contents.
 * Uses Web Crypto API with AES-256-GCM encryption.
 *
 * SECURITY NOTES:
 * - BIP39 wordlist is bundled locally to prevent MITM attacks
 * - Constant-time comparison used for checksum validation
 * - ECDH keys imported with minimal permissions
 */

// BIP39 wordlist bundled locally for security (prevents MITM attacks)
import { BIP39_WORDLIST } from './bip39-english';

// Crypto constants
const PBKDF2_ITERATIONS = 600000; // OWASP 2024 recommendation
const SALT_LENGTH = 32;
const IV_LENGTH = 12; // GCM standard
const KEY_LENGTH = 256;

/**
 * Constant-time string comparison to prevent timing attacks.
 * Both strings must be the same length for this to be secure.
 */
function constantTimeCompare(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

export interface EncryptedBlob {
  iv: Uint8Array;
  ciphertext: Uint8Array;
  version: number;
}

export interface VaultPayload {
  filename: string;
  mime_type: string;
  file_data: string; // base64 encoded
  thumbnail_data?: string; // base64 encoded
  caption?: string;
  album_name?: string;
  metadata?: {
    original_date?: string;
    location?: string;
    tags?: string[];
  };
}

export interface WrappedSeed {
  encryptedSeed: string; // base64
  salt: string; // base64
  version: number;
}

/**
 * Load BIP39 wordlist for mnemonic generation
 * SECURITY: Uses bundled wordlist to prevent MITM attacks
 */
async function loadBIP39Wordlist(): Promise<readonly string[]> {
  // Return bundled wordlist (no network request - prevents MITM attacks)
  return BIP39_WORDLIST;
}

/**
 * Generate a cryptographically secure master seed (32 bytes)
 */
export function generateMasterSeed(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Generate a random salt for key derivation
 */
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
}

/**
 * Generate a 12-word BIP39 mnemonic recovery phrase from entropy
 */
export async function generateRecoveryPhrase(entropy?: Uint8Array): Promise<string[]> {
  const wordlist = await loadBIP39Wordlist();

  // Use provided entropy or generate 128 bits (12 words)
  const entropyBytes = entropy || crypto.getRandomValues(new Uint8Array(16));

  // Convert entropy to binary string
  let binary = '';
  for (const byte of entropyBytes) {
    binary += byte.toString(2).padStart(8, '0');
  }

  // Calculate checksum (first 4 bits of SHA-256 hash)
  const hashBuffer = await crypto.subtle.digest('SHA-256', entropyBytes.buffer as ArrayBuffer);
  const hashArray = new Uint8Array(hashBuffer);
  const checksumBits = hashArray[0].toString(2).padStart(8, '0').slice(0, 4);

  // Append checksum to entropy
  const fullBinary = binary + checksumBits;

  // Split into 11-bit chunks and convert to words
  const words: string[] = [];
  for (let i = 0; i < 12; i++) {
    const chunk = fullBinary.slice(i * 11, (i + 1) * 11);
    const index = parseInt(chunk, 2);
    words.push(wordlist[index]);
  }

  return words;
}

/**
 * Convert recovery phrase back to entropy bytes
 */
export async function phraseToEntropy(words: string[]): Promise<Uint8Array> {
  if (words.length !== 12) {
    throw new Error('Recovery phrase must be exactly 12 words');
  }

  const wordlist = await loadBIP39Wordlist();

  // Convert words to binary
  let binary = '';
  for (const word of words) {
    const index = wordlist.indexOf(word.toLowerCase().trim());
    if (index === -1) {
      throw new Error(`Invalid word in recovery phrase: "${word}"`);
    }
    binary += index.toString(2).padStart(11, '0');
  }

  // Extract entropy (first 128 bits) and checksum (last 4 bits)
  const entropyBinary = binary.slice(0, 128);
  const checksumBinary = binary.slice(128);

  // Convert entropy binary to bytes
  const entropyBytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    entropyBytes[i] = parseInt(entropyBinary.slice(i * 8, (i + 1) * 8), 2);
  }

  // Verify checksum using constant-time comparison (prevents timing attacks)
  const hashBuffer = await crypto.subtle.digest('SHA-256', entropyBytes);
  const hashArray = new Uint8Array(hashBuffer);
  const expectedChecksum = hashArray[0].toString(2).padStart(8, '0').slice(0, 4);

  if (!constantTimeCompare(checksumBinary, expectedChecksum)) {
    throw new Error('Invalid recovery phrase checksum');
  }

  return entropyBytes;
}

/**
 * Derive an AES-256 key from a PIN using PBKDF2
 */
export async function deriveKeyFromPIN(
  pin: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const pinBytes = new TextEncoder().encode(pin);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    pinBytes.buffer as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['wrapKey', 'unwrapKey']
  );
}

/**
 * Derive an AES-256 key from a recovery phrase
 */
export async function deriveKeyFromPhrase(words: string[]): Promise<CryptoKey> {
  const entropy = await phraseToEntropy(words);

  // Use entropy directly as key material (already 128 bits of entropy)
  // Stretch it with PBKDF2 for additional security
  const salt = new TextEncoder().encode('momento-recovery-v1');

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    entropy.buffer as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['wrapKey', 'unwrapKey']
  );
}

/**
 * Derive the vault encryption key from the master seed
 * SECURITY: Use PBKDF2 with 100k iterations for key derivation.
 * Even though master seed has strong entropy, additional iterations
 * provide defense-in-depth against potential seed compromise.
 */
export async function deriveVaultKey(masterSeed: Uint8Array): Promise<CryptoKey> {
  const salt = new TextEncoder().encode('momento-vault-key-v1');

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    masterSeed.buffer as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: 100000, // SECURITY FIX: Was 1, now 100k for defense-in-depth
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Wrap (encrypt) the master seed with a derived key
 */
export async function wrapMasterSeed(
  masterSeed: Uint8Array,
  wrappingKey: CryptoKey
): Promise<WrappedSeed> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const salt = generateSalt();

  // Import master seed as a raw key for wrapping
  const seedKey = await crypto.subtle.importKey(
    'raw',
    masterSeed.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: KEY_LENGTH },
    true, // extractable for wrapping
    ['encrypt', 'decrypt']
  );

  // Wrap the key
  const wrappedKey = await crypto.subtle.wrapKey(
    'raw',
    seedKey,
    wrappingKey,
    { name: 'AES-GCM', iv }
  );

  // Combine IV and wrapped key
  const combined = new Uint8Array(IV_LENGTH + wrappedKey.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(wrappedKey), IV_LENGTH);

  return {
    encryptedSeed: uint8ArrayToBase64(combined),
    salt: uint8ArrayToBase64(salt),
    version: 1
  };
}

/**
 * Unwrap (decrypt) the master seed with a derived key
 */
export async function unwrapMasterSeed(
  wrapped: WrappedSeed,
  unwrappingKey: CryptoKey
): Promise<Uint8Array> {
  const combined = base64ToUint8Array(wrapped.encryptedSeed);

  const iv = combined.slice(0, IV_LENGTH);
  const wrappedKey = combined.slice(IV_LENGTH);

  try {
    const seedKey = await crypto.subtle.unwrapKey(
      'raw',
      wrappedKey,
      unwrappingKey,
      { name: 'AES-GCM', iv },
      { name: 'AES-GCM', length: KEY_LENGTH },
      true,
      ['encrypt', 'decrypt']
    );

    // Export the unwrapped key as raw bytes
    const exported = await crypto.subtle.exportKey('raw', seedKey);
    return new Uint8Array(exported);
  } catch (error) {
    throw new Error('Failed to unlock vault - wrong PIN or corrupted data');
  }
}

/**
 * Encrypt a vault item payload
 */
export async function encryptVaultItem(
  payload: VaultPayload,
  vaultKey: CryptoKey
): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    vaultKey,
    payloadBytes
  );

  // Prepend IV to encrypted data
  const result = new Uint8Array(IV_LENGTH + encrypted.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(encrypted), IV_LENGTH);

  return result;
}

/**
 * Decrypt a vault item payload
 */
export async function decryptVaultItem(
  encryptedBlob: Uint8Array,
  vaultKey: CryptoKey
): Promise<VaultPayload> {
  const iv = encryptedBlob.slice(0, IV_LENGTH);
  const ciphertext = encryptedBlob.slice(IV_LENGTH);

  try {
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      vaultKey,
      ciphertext
    );

    const payloadString = new TextDecoder().decode(decrypted);
    return JSON.parse(payloadString);
  } catch (error) {
    throw new Error('Failed to decrypt vault item - corrupted or wrong key');
  }
}

/**
 * Encrypt raw file data (for direct file encryption)
 */
export async function encryptFile(
  fileData: ArrayBuffer,
  vaultKey: CryptoKey
): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    vaultKey,
    fileData
  );

  const result = new Uint8Array(IV_LENGTH + encrypted.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(encrypted), IV_LENGTH);

  return result;
}

/**
 * Decrypt raw file data
 */
export async function decryptFile(
  encryptedData: Uint8Array,
  vaultKey: CryptoKey
): Promise<ArrayBuffer> {
  const iv = encryptedData.slice(0, IV_LENGTH);
  const ciphertext = encryptedData.slice(IV_LENGTH);

  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    vaultKey,
    ciphertext
  );
}

// Utility functions for base64 encoding/decoding
export function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Validate a PIN format (6 digits)
 */
export function validatePIN(pin: string): { valid: boolean; error?: string } {
  if (!pin) {
    return { valid: false, error: 'PIN is required' };
  }
  if (!/^\d{6}$/.test(pin)) {
    return { valid: false, error: 'PIN must be exactly 6 digits' };
  }
  // Check for weak PINs
  const weakPins = ['000000', '111111', '222222', '333333', '444444',
                    '555555', '666666', '777777', '888888', '999999',
                    '123456', '654321', '123123'];
  if (weakPins.includes(pin)) {
    return { valid: false, error: 'This PIN is too easy to guess' };
  }
  return { valid: true };
}

/**
 * Get autocomplete suggestions for recovery phrase words
 */
export async function getWordSuggestions(partial: string): Promise<string[]> {
  if (!partial || partial.length < 2) return [];

  const wordlist = await loadBIP39Wordlist();
  const lower = partial.toLowerCase();

  return wordlist
    .filter(word => word.startsWith(lower))
    .slice(0, 5);
}

/**
 * Validate a complete recovery phrase
 */
export async function validateRecoveryPhrase(words: string[]): Promise<{ valid: boolean; error?: string }> {
  if (words.length !== 12) {
    return { valid: false, error: 'Recovery phrase must be exactly 12 words' };
  }

  try {
    await phraseToEntropy(words);
    return { valid: true };
  } catch (error) {
    return { valid: false, error: (error as Error).message };
  }
}

// ============ ECDH Hybrid Encryption ============
// Allows encryption without PIN (using public key only)
// Decryption requires PIN (to access private key)

export interface ECDHKeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

export interface ExportedECDHKeys {
  publicKey: string; // base64 encoded SPKI format
  wrappedPrivateKey: string; // base64 encoded (IV + wrapped key)
}

export interface ECDHEncryptedData {
  ephemeralPublicKey: string; // base64 encoded SPKI
  iv: string; // base64 encoded IV
  ciphertext: string; // base64 encoded encrypted data
  version: number;
}

/**
 * Generate an ECDH P-256 key pair for hybrid encryption
 */
export async function generateECDHKeyPair(): Promise<ECDHKeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'ECDH',
      namedCurve: 'P-256',
    },
    true, // extractable (needed for export/wrap)
    ['deriveKey', 'deriveBits']
  );

  return {
    publicKey: keyPair.publicKey,
    privateKey: keyPair.privateKey,
  };
}

/**
 * Export ECDH public key to base64 string
 */
export async function exportECDHPublicKey(publicKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey('spki', publicKey);
  return uint8ArrayToBase64(new Uint8Array(exported));
}

/**
 * Import ECDH public key from base64 string
 * SECURITY: extractable=false prevents key extraction, [] usage is correct for public keys
 */
export async function importECDHPublicKey(publicKeyBase64: string): Promise<CryptoKey> {
  const keyData = base64ToUint8Array(publicKeyBase64);
  return crypto.subtle.importKey(
    'spki',
    keyData.buffer as ArrayBuffer,
    { name: 'ECDH', namedCurve: 'P-256' },
    false, // SECURITY FIX: Not extractable
    []     // Correct: public keys don't need usage flags for ECDH
  );
}

/**
 * Wrap (encrypt) ECDH private key with PIN-derived AES key
 */
export async function wrapECDHPrivateKey(
  privateKey: CryptoKey,
  wrappingKey: CryptoKey
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const wrapped = await crypto.subtle.wrapKey(
    'pkcs8',
    privateKey,
    wrappingKey,
    { name: 'AES-GCM', iv }
  );

  // Combine IV + wrapped key
  const combined = new Uint8Array(IV_LENGTH + wrapped.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(wrapped), IV_LENGTH);

  return uint8ArrayToBase64(combined);
}

/**
 * Unwrap (decrypt) ECDH private key with PIN-derived AES key
 */
export async function unwrapECDHPrivateKey(
  wrappedKeyBase64: string,
  unwrappingKey: CryptoKey
): Promise<CryptoKey> {
  const combined = base64ToUint8Array(wrappedKeyBase64);
  const iv = combined.slice(0, IV_LENGTH);
  const wrappedKey = combined.slice(IV_LENGTH);

  return crypto.subtle.unwrapKey(
    'pkcs8',
    wrappedKey,
    unwrappingKey,
    { name: 'AES-GCM', iv },
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
}

/**
 * Derive AES key from ECDH shared secret
 */
async function deriveAESKeyFromECDH(
  privateKey: CryptoKey,
  publicKey: CryptoKey
): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    {
      name: 'ECDH',
      public: publicKey,
    },
    privateKey,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt data using ECDH hybrid encryption (no PIN needed)
 * Uses vault's public key + ephemeral keypair
 * SECURITY: Ephemeral private key is cleared after use (best-effort)
 */
export async function encryptWithECDH(
  data: Uint8Array,
  vaultPublicKey: CryptoKey
): Promise<ECDHEncryptedData> {
  // Generate ephemeral keypair for forward secrecy
  const ephemeralKeyPair = await generateECDHKeyPair();

  try {
    // Derive shared secret using ephemeral private + vault public
    const sharedKey = await deriveAESKeyFromECDH(
      ephemeralKeyPair.privateKey,
      vaultPublicKey
    );

    // Encrypt with derived AES key
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
      sharedKey,
      data.buffer as ArrayBuffer
    );

    // Export ephemeral public key (needed for decryption)
    const ephemeralPublicKeyBase64 = await exportECDHPublicKey(ephemeralKeyPair.publicKey);

    return {
      ephemeralPublicKey: ephemeralPublicKeyBase64,
      iv: uint8ArrayToBase64(iv),
      ciphertext: uint8ArrayToBase64(new Uint8Array(encrypted)),
      version: 2, // ECDH version
    };
  } finally {
    // SECURITY: Best-effort clear ephemeral private key from memory
    // Note: JS has no true secure memory wiping, but we null the reference
    // to allow faster GC and reduce exposure window
    (ephemeralKeyPair as { privateKey: CryptoKey | null }).privateKey = null;
  }
}

/**
 * Decrypt data using ECDH hybrid encryption (requires private key)
 * Uses vault's private key + stored ephemeral public key
 */
export async function decryptWithECDH(
  encryptedData: ECDHEncryptedData,
  vaultPrivateKey: CryptoKey
): Promise<Uint8Array> {
  // Import ephemeral public key
  const ephemeralPublicKey = await importECDHPublicKey(encryptedData.ephemeralPublicKey);

  // Derive shared secret using vault private + ephemeral public
  const sharedKey = await deriveAESKeyFromECDH(
    vaultPrivateKey,
    ephemeralPublicKey
  );

  // Decrypt
  const iv = base64ToUint8Array(encryptedData.iv);
  const ciphertext = base64ToUint8Array(encryptedData.ciphertext);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    sharedKey,
    ciphertext.buffer as ArrayBuffer
  );

  return new Uint8Array(decrypted);
}

/**
 * Serialize ECDH encrypted data to a single Uint8Array for storage
 * Format: [version(1)] [ephemeralKeyLength(2)] [ephemeralKey] [iv(12)] [ciphertext]
 */
export function serializeECDHEncrypted(data: ECDHEncryptedData): Uint8Array {
  const ephemeralKey = base64ToUint8Array(data.ephemeralPublicKey);
  const iv = base64ToUint8Array(data.iv);
  const ciphertext = base64ToUint8Array(data.ciphertext);

  const result = new Uint8Array(1 + 2 + ephemeralKey.length + iv.length + ciphertext.length);
  let offset = 0;

  // Version byte
  result[offset++] = data.version;

  // Ephemeral key length (2 bytes, big-endian)
  result[offset++] = (ephemeralKey.length >> 8) & 0xff;
  result[offset++] = ephemeralKey.length & 0xff;

  // Ephemeral key
  result.set(ephemeralKey, offset);
  offset += ephemeralKey.length;

  // IV (fixed 12 bytes)
  result.set(iv, offset);
  offset += iv.length;

  // Ciphertext
  result.set(ciphertext, offset);

  return result;
}

/**
 * Deserialize ECDH encrypted data from Uint8Array
 */
export function deserializeECDHEncrypted(data: Uint8Array): ECDHEncryptedData {
  let offset = 0;

  // Version byte
  const version = data[offset++];

  // Ephemeral key length
  const ephemeralKeyLength = (data[offset] << 8) | data[offset + 1];
  offset += 2;

  // Ephemeral key
  const ephemeralKey = data.slice(offset, offset + ephemeralKeyLength);
  offset += ephemeralKeyLength;

  // IV (12 bytes)
  const iv = data.slice(offset, offset + IV_LENGTH);
  offset += IV_LENGTH;

  // Ciphertext (rest)
  const ciphertext = data.slice(offset);

  return {
    ephemeralPublicKey: uint8ArrayToBase64(ephemeralKey),
    iv: uint8ArrayToBase64(iv),
    ciphertext: uint8ArrayToBase64(ciphertext),
    version,
  };
}

// Export singleton-style crypto service
export const cryptoService = {
  // Original symmetric encryption
  generateMasterSeed,
  generateSalt,
  generateRecoveryPhrase,
  phraseToEntropy,
  deriveKeyFromPIN,
  deriveKeyFromPhrase,
  deriveVaultKey,
  wrapMasterSeed,
  unwrapMasterSeed,
  encryptVaultItem,
  decryptVaultItem,
  encryptFile,
  decryptFile,
  validatePIN,
  getWordSuggestions,
  validateRecoveryPhrase,
  uint8ArrayToBase64,
  base64ToUint8Array,
  // ECDH hybrid encryption (for "click and save" without PIN)
  generateECDHKeyPair,
  exportECDHPublicKey,
  importECDHPublicKey,
  wrapECDHPrivateKey,
  unwrapECDHPrivateKey,
  encryptWithECDH,
  decryptWithECDH,
  serializeECDHEncrypted,
  deserializeECDHEncrypted,
};

export default cryptoService;
